import { ObjectIdLookup } from 'src/app/models/objectIdLookup.model';
import { RolePermission } from 'src/app/models/rolePermission.model';
import { diffRolePermissions } from './role-permission-diff.util';
import { getObjectDisplayName } from './role-permission-grouping.util';

/**
 * One object whose persisted mask diverged from what the save posted (DASH-3179, part 8/9;
 * design-spec §7 state 8, §8). Carries the product `name` so the divergence banner never renders the
 * raw `GLOBAL_*` / `COID_*` id (design-spec §9, DS-13).
 */
export interface DivergentObject {
  objectId: string;
  name: string;
}

/**
 * What the server actually stored, compared to what the save posted (design-spec §8).
 *
 * - `objectIds` backs the per-row amber edge kept on the matrix until the banner is dismissed.
 * - `objects` is the named, display-ready list for the divergence banner, in catalogue order.
 */
export interface SaveDivergence {
  objectIds: ReadonlySet<string>;
  objects: DivergentObject[];
}

/** No divergence — the read-back matched the posted payload exactly. */
export const EMPTY_SAVE_DIVERGENCE: SaveDivergence = {
  objectIds: new Set<string>(),
  objects: [],
};

/**
 * Compares the mask the save posted against the mask the server persisted (the mandatory read-back,
 * design-spec §8) and lists the objects the server rewrote — the `StripUnentitledAnalystPermissionsAsync`
 * reality made visible rather than hidden behind a success toast (§7 state 8).
 *
 * Divergence is the diff util's per-verb comparison of posted vs persisted, so it never depends on the raw
 * bit layout and a scope-only rewrite counts too. Only objects present in the catalogue are named — the
 * banner must never fall back to a raw id — and the list follows catalogue order so it reads predictably.
 */
export function computeSaveDivergence(
  posted: readonly RolePermission[] | null | undefined,
  persisted: readonly RolePermission[] | null | undefined,
  objects: readonly ObjectIdLookup[],
): SaveDivergence {
  const divergentIds = diffRolePermissions(posted, persisted).dirtyObjectIds;
  if (!divergentIds.size) {
    return EMPTY_SAVE_DIVERGENCE;
  }
  const named = objects
    .filter(object => divergentIds.has(object.id))
    .map<DivergentObject>(object => ({ objectId: object.id, name: getObjectDisplayName(object) }));
  return { objectIds: divergentIds, objects: named };
}
